/** Shared GFM (GitHub-flavored markdown) table parsing for MarkdownView and CSV export. */

export type ExtractedMarkdownTable = {
  startIdx: number;
  endIdx: number;
  header: string[];
  rows: string[][];
};

/** Split a markdown table line into trimmed cell strings, or null if not a `|`-delimited row. */
export function splitTableRow(line: string): string[] | null {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) {
    return null;
  }
  let inner = trimmed;
  if (inner.startsWith('|')) {
    inner = inner.slice(1);
  }
  if (inner.endsWith('|')) {
    inner = inner.slice(0, -1);
  }
  return inner.split('|').map((cell) => cell.trim());
}

/** A GFM separator row, e.g. `|---|:--:|`. */
export function isSeparatorRow(line: string): boolean {
  const cells = splitTableRow(line);
  if (!cells || cells.length === 0) {
    return false;
  }
  return cells.every((cell) => /^:?-+:?$/.test(cell.replace(/\s/g, '')));
}

export function isTableRow(line: string): boolean {
  return line.includes('|') && splitTableRow(line) !== null && !isSeparatorRow(line);
}

/** Find all GFM tables (`header / separator / rows…`) within a list of lines. */
export function extractAllTables(lines: string[]): ExtractedMarkdownTable[] {
  const tables: ExtractedMarkdownTable[] = [];
  let k = 0;
  while (k < lines.length - 1) {
    if (isTableRow(lines[k]) && isSeparatorRow(lines[k + 1])) {
      const header = splitTableRow(lines[k]) ?? [];
      const rows: string[][] = [];
      let t = k + 2;
      while (t < lines.length && isTableRow(lines[t])) {
        rows.push(splitTableRow(lines[t]) ?? []);
        t++;
      }
      tables.push({startIdx: k, endIdx: t, header, rows});
      k = t;
    } else {
      k += 1;
    }
  }
  return tables;
}

/** Find the first GFM table (`header / separator / rows…`) within a list of lines. */
export function extractTable(lines: string[]): ExtractedMarkdownTable | null {
  return extractAllTables(lines)[0] ?? null;
}

/** Parse the first GFM table in a markdown string, or null if none. */
export function parseFirstMarkdownTable(
  markdown: string
): {header: string[]; rows: string[][]} | null {
  const table = extractTable(markdown.split('\n'));
  if (!table || table.header.length === 0) {
    return null;
  }
  return {header: table.header, rows: table.rows};
}
